import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Pencil, X, Loader2, Instagram, Globe, Download, ShieldCheck, AlertTriangle, Sparkles } from "lucide-react";

const EMPTY = {
  name: "",
  instagram_handle: "",
  website_url: "",
  city: "",
  color: "#14342B",
  description: "",
  privacy_confirmed: false,
  ai_tone: "streetwear",
  ai_language: "français",
  ai_emoji: "parcimonie",
  ai_autonomy: "assisté",
  ai_instructions: "",
};

const AUTONOMY_OPTIONS = [
  { id: "manuel", label: "Manuel", desc: "L'IA prépare des brouillons uniquement. Tu valides et publies." },
  { id: "assisté", label: "Assisté", desc: "L'IA crée et planifie. Publication immédiate confirmée par toi." },
  { id: "autonome", label: "Autonome", desc: "L'IA fait tout, y compris publier seule sur Instagram." },
];

const PRESET_COLORS = [
  "#14342B", "#1E3A5F", "#4A2C4D", "#5C4033", "#2B2B2B", "#0F5257",
  "#CC4B37", "#E07A5F", "#D48A29", "#E4B429", "#7A9E3F", "#2E8B57",
  "#1CA6A6", "#2F6FED", "#6C5CE7", "#9B5DE5", "#C9257E", "#E84393",
  "#B08968", "#8D99AE", "#3D348B", "#06A77D", "#F25F5C", "#111111",
];

export default function Clients() {
  const { clients, refresh, setActive, activeId } = useClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...EMPTY, ...c });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Le nom de la marque est requis");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Marque mise à jour");
      } else {
        const { data } = await api.post("/clients", form);
        toast.success("Marque ajoutée");
        setActive(data.id);
      }
      await refresh();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const exportData = async (id, name) => {
    setExportingId(id);
    try {
      const { data } = await api.get(`/privacy/export/${id}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `natye-export-${name.replace(/\s+/g, "-").toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Données exportées (JSON)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setExportingId(null);
  };

  const confirmFullDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toUpperCase() !== "SUPPRIMER")
      return toast.error('Tape "SUPPRIMER" pour confirmer');
    setDeleting(true);
    try {
      await api.delete(`/clients/${deleteTarget.id}/full`);
      toast.success("Marque et toutes ses données supprimées définitivement");
      setDeleteTarget(null);
      setDeleteConfirm("");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setDeleting(false);
  };

  const field =
    "w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors";

  return (
    <>
      <PageHeader overline="Portefeuille · Agence" title="Marques Clients">
        <button
          data-testid="add-client-button"
          onClick={openCreate}
          className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150"
        >
          <Plus className="h-4 w-4" />
          Nouvelle marque
        </button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-list">
          {clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-card-${c.id}`}
              className={`border rounded-sm bg-secondary p-5 transition-colors ${
                activeId === c.id ? "border-accent/50" : "border-white/10"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="h-10 w-10 rounded-sm flex items-center justify-center font-heading font-black text-lg uppercase"
                  style={{ background: c.color }}
                >
                  {c.name[0]}
                </div>
                <div className="flex gap-1">
                  <button
                    data-testid={`edit-client-${c.id}`}
                    onClick={() => openEdit(c)}
                    className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`export-client-${c.id}`}
                    onClick={() => exportData(c.id, c.name)}
                    disabled={exportingId === c.id}
                    title="Exporter les données (RGPD)"
                    className="p-1.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    {exportingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </button>
                  <button
                    data-testid={`delete-client-${c.id}`}
                    onClick={() => { setDeleteTarget(c); setDeleteConfirm(""); }}
                    title="Suppression définitive (RGPD)"
                    className="p-1.5 text-muted-foreground hover:text-accent transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight">{c.name}</h3>
              {c.instagram_handle && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                  <Instagram className="h-3.5 w-3.5" /> @{c.instagram_handle}
                </p>
              )}
              {c.website_url && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 truncate">
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{c.website_url.replace(/^https?:\/\//, "")}</span>
                </p>
              )}
              <button
                onClick={() => setActive(c.id)}
                disabled={activeId === c.id}
                className="mt-4 w-full border border-white/15 rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors disabled:opacity-40"
              >
                {activeId === c.id ? "Marque active" : "Travailler dessus"}
              </button>
            </div>
          ))}

          <button
            onClick={openCreate}
            className="border border-dashed border-white/15 rounded-sm p-5 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors min-h-[180px]"
          >
            <Building2 className="h-6 w-6" />
            <span className="text-xs font-bold uppercase tracking-wider">Ajouter une marque</span>
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            data-testid="client-form"
            className="bg-[#111111] border border-white/15 rounded-sm w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold tracking-tight">
                {editing ? "Modifier la marque" : "Nouvelle marque"}
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Nom de la marque *
                </label>
                <input
                  data-testid="client-name-input"
                  className={field}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nom de la marque"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                    Compte Instagram
                  </label>
                  <input
                    data-testid="client-handle-input"
                    className={field}
                    value={form.instagram_handle}
                    onChange={(e) => setForm({ ...form, instagram_handle: e.target.value.replace("@", "") })}
                    placeholder="compte.instagram"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                    Ville
                  </label>
                  <input
                    className={field}
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Ville, Pays"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Site web (source des visuels)
                </label>
                <input
                  data-testid="client-website-input"
                  className={field}
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  placeholder="https://www.site-de-la-marque.com"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Univers de la marque (aide l'IA)
                </label>
                <textarea
                  data-testid="client-description-input"
                  rows={3}
                  className={field}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ton, valeurs, style, cible… Décris l'univers de la marque pour guider l'IA."
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Couleur
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((col) => (
                    <button
                      key={col}
                      type="button"
                      onClick={() => setForm({ ...form, color: col })}
                      className={`h-8 w-8 rounded-sm border-2 transition-transform ${
                        form.color === col ? "border-white scale-110" : "border-transparent"
                      }`}
                      style={{ background: col }}
                    />
                  ))}
                  <label
                    className="h-8 w-8 rounded-sm border border-white/20 flex items-center justify-center cursor-pointer overflow-hidden relative"
                    title="Couleur personnalisée"
                  >
                    <span className="text-[10px] font-bold text-muted-foreground">+</span>
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </label>
                </div>
              </div>

              {/* Profil IA */}
              <div className="border-t border-white/10 pt-5 space-y-4" data-testid="client-ai-profile">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" /> Profil IA · NATYE-IA
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                      Ton éditorial
                    </label>
                    <input
                      data-testid="client-ai-tone-input"
                      className={field}
                      value={form.ai_tone}
                      onChange={(e) => setForm({ ...form, ai_tone: e.target.value })}
                      placeholder="streetwear, luxe, décontracté…"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                      Langue
                    </label>
                    <input
                      data-testid="client-ai-language-input"
                      className={field}
                      value={form.ai_language}
                      onChange={(e) => setForm({ ...form, ai_language: e.target.value })}
                      placeholder="français"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                    Emojis
                  </label>
                  <div className="flex gap-2">
                    {["aucun", "parcimonie", "beaucoup"].map((e) => (
                      <button
                        key={e}
                        type="button"
                        data-testid={`client-ai-emoji-${e}`}
                        onClick={() => setForm({ ...form, ai_emoji: e })}
                        className={`flex-1 px-2 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                          form.ai_emoji === e
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                    Niveau d'autonomie de l'IA
                  </label>
                  <div className="space-y-2">
                    {AUTONOMY_OPTIONS.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        data-testid={`client-ai-autonomy-${o.id}`}
                        onClick={() => setForm({ ...form, ai_autonomy: o.id })}
                        className={`w-full text-left border rounded-sm px-3 py-2.5 transition-colors ${
                          form.ai_autonomy === o.id
                            ? "border-accent bg-accent/10"
                            : "border-white/15 hover:border-white/30"
                        }`}
                      >
                        <span className="text-xs font-bold uppercase tracking-wider">{o.label}</span>
                        <span className="block text-[11px] text-muted-foreground mt-0.5">{o.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                    Consignes spécifiques pour l'IA (optionnel)
                  </label>
                  <textarea
                    data-testid="client-ai-instructions-input"
                    rows={2}
                    className={field}
                    value={form.ai_instructions}
                    onChange={(e) => setForm({ ...form, ai_instructions: e.target.value })}
                    placeholder="Ex : toujours mentionner la livraison offerte, éviter les promos agressives, tutoyer la communauté…"
                  />
                </div>
              </div>

              <label
                className="flex items-start gap-3 border border-white/10 rounded-sm p-4 bg-background/40 cursor-pointer"
                data-testid="privacy-confirm-label"
              >
                <input
                  data-testid="client-privacy-confirmed-input"
                  type="checkbox"
                  checked={!!form.privacy_confirmed}
                  onChange={(e) => setForm({ ...form, privacy_confirmed: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-[#E8B527] shrink-0"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  <b className="text-foreground">Droits d'utilisation des médias confirmés.</b> Je certifie
                  disposer des droits nécessaires sur les visuels et contenus de cette marque, et être
                  autorisé(e) à les traiter et publier via NATYE (RGPD).
                </span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 border border-white/15 rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                data-testid="save-client-button"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Enregistrer" : "Créer"}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div
            data-testid="delete-client-modal"
            className="bg-[#111111] border border-accent/40 rounded-sm w-full max-w-md p-6"
          >
            <div className="flex items-center gap-2 mb-4 text-accent">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Suppression définitive</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Cette action supprimera <b className="text-foreground">définitivement</b> la marque
              «&nbsp;{deleteTarget.name}&nbsp;» ainsi que <b>tous ses produits, posts et visuels IA</b>
              &nbsp;(base de données et stockage). Cette opération est <b>irréversible</b>.
            </p>
            <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
              Tape « SUPPRIMER » pour confirmer
            </label>
            <input
              data-testid="delete-confirm-input"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="SUPPRIMER"
              className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent focus:border-accent transition-colors mb-6"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}
                className="flex-1 border border-white/15 rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                data-testid="confirm-delete-client-button"
                onClick={confirmFullDelete}
                disabled={deleting || deleteConfirm.trim().toUpperCase() !== "SUPPRIMER"}
                className="flex-1 bg-accent text-white rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-accent/80 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
